import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { aboutMilestones, aboutPrinciples, siteInfo, testimonials } from "@/mocks/site";

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们｜网红事件复盘的方法与原则 - 吃瓜复盘局",
    description:
      "吃瓜复盘局专注网红事件完整经过与深度解析。这里介绍我们的品牌定位、内容原则、复盘方法与联系方式，说明每篇事件复盘是如何被核查与整理的。",
    keywords: "关于吃瓜复盘局,网红事件复盘方法,内容原则,事件解析团队",
    canonical: `${siteInfo.domain}/about`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜复盘局",
        url: `${siteInfo.domain}/about`,
        description: siteInfo.shortDesc,
        isPartOf: { "@type": "WebSite", url: `${siteInfo.domain}/` },
        mainEntity: {
          "@type": "Organization",
          name: siteInfo.name,
          url: `${siteInfo.domain}/`,
          email: siteInfo.email,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${siteInfo.domain}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${siteInfo.domain}/about` },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            关于吃瓜复盘局
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            一个把网红事件完整经过整理成时间线的内容站点。我们相信，看清顺序比急于表态更重要。
          </p>
        </div>
      </div>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-16">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="h-72 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20flat%20lay%20of%20a%20desk%20with%20timeline%20cards%2C%20sticky%20notes%20and%20a%20magnifying%20glass%2C%20cream%20terracotta%20and%20olive%20palette%2C%20soft%20paper%20texture%2C%20minimal%20aesthetic%2C%20no%20readable%20text&width=1200&height=900&seq=cf-about-2026&orientation=landscape"
              alt="吃瓜复盘局的内容整理工作场景"
              title="吃瓜复盘局 - 网红事件完整经过整理"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              我们是谁
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              从热搜碎片，到一条能读懂的时间线
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-600 md:text-[15px]">
              {siteInfo.intro}
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-[15px]">
              我们不做道德审判，也不追求抢发。相比谁先开口，我们更在意信息是否完整、顺序是否准确、当事各方的说法有没有被平等呈现。这也是我们把复盘结构固定下来的原因。
            </p>
            <div className="mt-7 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <p className="font-heading text-xl font-bold text-primary-600">{siteInfo.foundedYear}</p>
                <p className="mt-1 text-xs text-foreground-500">开始整理事件</p>
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-primary-600">386</p>
                <p className="mt-1 text-xs text-foreground-500">累计复盘篇数</p>
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-primary-600">4</p>
                <p className="mt-1 text-xs text-foreground-500">固定复盘步骤</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <SectionHeading
            eyebrow="内容原则"
            title="我们写每一篇复盘时守的规矩"
            desc="这些原则公开写在这里，也欢迎读者按它来对照我们的内容。"
          />
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {aboutPrinciples.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-5 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <SectionHeading
            eyebrow="发展理念"
            title="从一个个人博客开始"
            desc="我们没有宏大的目标，只是想让追热点这件事少一点情绪消耗，多一点可验证的信息。"
          />
          <ol className="mt-10 grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {aboutMilestones.map((item, index) => (
              <li key={item.year} className="relative">
                <span className="font-heading text-3xl font-bold text-primary-200">{item.year}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                {index < aboutMilestones.length - 1 && (
                  <span className="absolute right-0 top-4 hidden h-px w-10 bg-background-300 lg:block"></span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <SectionHeading eyebrow="用户口碑" title="读者留下的反馈" />
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <div className="flex items-center gap-1 text-accent-600">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <i
                      key={index}
                      className={`${index < item.rating ? "ri-star-fill" : "ri-star-line"} text-sm`}
                    ></i>
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-7 text-foreground-700">
                  {item.content}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500 font-heading text-sm font-bold text-background-50">
                    {item.initial}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <div className="grid items-center gap-8 rounded-lg bg-primary-900 p-8 md:p-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-background-100/80">
                内容纠错、线索提供、合作咨询，都可以直接发邮件。我们通常在两个工作日内回复，涉及核实的内容会先与提供者确认来源。
              </p>
              <p className="mt-5 text-sm text-background-100/80">
                邮箱：
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="text-accent-300 underline-offset-4 hover:underline"
                >
                  {siteInfo.email}
                </a>
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/events"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
              >
                浏览事件库
                <i className="ri-arrow-right-line"></i>
              </Link>
              <p className="mt-4 text-xs text-background-100/70">{siteInfo.updateFrequency}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}